/**
 * The eight amosWEB `Kategorie` values, so a group reads the same in both products.
 * Kept in sync with App\Models\Events\ParticipantGroupColor.
 *
 * Only the `-500` step is safelisted in tailwind.config.ts (`/(bg|text)-(.*)-500/`), so the map must stay on it —
 * any other step is purged from the build and renders as nothing.
 */
export const participantGroupColors = ['black', 'blue', 'cyan', 'green', 'yellow', 'orange', 'red', 'pink'] as const

export type ParticipantGroupColor = typeof participantGroupColors[number]

const backgrounds: Record<string, string> = {
    black: 'bg-gray-700',
    blue: 'bg-blue-500',
    cyan: 'bg-cyan-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500',
    pink: 'bg-pink-500',
}

const texts: Record<string, string> = {
    black: 'text-gray-700',
    blue: 'text-blue-500',
    cyan: 'text-cyan-500',
    green: 'text-green-500',
    yellow: 'text-yellow-500',
    orange: 'text-orange-500',
    red: 'text-red-500',
    pink: 'text-pink-500',
}

/**
 * An unknown value is rendered neutrally rather than dropped: amosWEB may add a ninth colour at any time, and the
 * backend deliberately stores whatever it sends (see the 293 spec, §3.1).
 */
export function useParticipantGroupColor() {
    const backgroundClass = (color?: string | null): string => backgrounds[color ?? ''] ?? 'bg-gray-300'
    const textClass = (color?: string | null): string => texts[color ?? ''] ?? 'text-gray-400'
    const isKnown = (color?: string | null): boolean => !!color && color in backgrounds

    return {colors: participantGroupColors, backgroundClass, textClass, isKnown}
}
